import { LinkRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { SmallInputLabel, StrategicContextPicker } from "..";
import { ActionItemTeamPlanSectionDto, GetPlanActionItemDto, useEditActionItemContext } from "../../http";

interface IAddSubStrategyFormState {
  selectedSubItem: GetPlanActionItemDto | null;
}

interface IProps {
  visible: boolean;
  teamPlanSection: ActionItemTeamPlanSectionDto;
  context: {
    teamName: string;
    teamSlug: string;
    actionItemGuid: string;
    actionItemDescription: string;
  };
  onCreated: () => void;
  onCancelled: () => void;
}

const AddSubStrategyItemModal = (props: IProps) => {
  const { mutate: editActionItemContext, isLoading: isUpdating } = useEditActionItemContext();

  const { formState, control, handleSubmit, reset } = useForm<IAddSubStrategyFormState>({
    defaultValues: {
      selectedSubItem: null,
    },
  });

  async function handleDoneClicked() {
    handleSubmit((formState) => {
      if (!formState.selectedSubItem) {
        return;
      }

      editActionItemContext({
        actionItemGuid: formState.selectedSubItem?.actionItemGuid,
        contextActionItemGuid: props.context.actionItemGuid,
        currentContextActionItemGuid: formState.selectedSubItem?.contextActionItemGuid ?? null,
        planGuid: props.teamPlanSection.planGuid,
      });
      props.onCreated();
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Connect Existing Strategic Item As Subitem</DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        <Stack spacing={1}>
          <Stack sx={{ px: 1 }}>
            <SmallInputLabel>Context</SmallInputLabel>
            <Stack direction="row" spacing={0.25} sx={{ alignItems: "start" }}>
              <LinkRounded sx={{ position: "relative", top: 4 }} />
              <Typography>
                <Box component="span" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  {props.context?.teamName}
                </Box>
                {` : ${props.context?.actionItemDescription}`}
              </Typography>
            </Stack>
          </Stack>

          <Controller
            name="selectedSubItem"
            control={control}
            rules={{ required: "A strategic item must be selected" }}
            render={({ field }) => (
              <StrategicContextPicker
                actionItemGuid={props.context.actionItemGuid}
                shouldHideParents
                teamOptionDisabled={(team) => !(team.isMember || team.isAdministrator)}
                onSelect={(connectionPoint) => field.onChange(connectionPoint)}
                showLabels
                sx={{ px: 1 }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isUpdating} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isUpdating || !formState.isValid}
          endIcon={isUpdating ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleDoneClicked}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AddSubStrategyItemModal };
